import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  HiPlus,
  HiChatBubbleOvalLeft,
  HiSparkles,
  HiEllipsisHorizontal,
  HiPencil,
  HiTrash,
  HiCheck,
  HiXMark,
} from "react-icons/hi2";
import { DayPost, CommentWithUser, ActivityLog, TrainingLog, } from "../../types/post";
import TrainingLogCard from "./TrainingLogCard";
import PerformanceCard from "./PerformanceCard";
import NoteLogCard from "./NoteLogCard";
import PerformanceLogCard from "./PerformanceLogCard";
import { useUserEvents } from "../../hooks/useUserEvents";
import { trainingTypeService } from "../../services/trainingTypeService";
import RecoveryLogCard from "./RecoveryLogCard";
import NiggleLogCard from "./NiggleLogCard";
import HealthLogCard from "./HealthLogCard";
import StepLogCard from "./StepLogCard";
import WellnessLogCard from "./WellnessLogCard";
import NegativeInfluencesLogCard from "./NegativeInfluencesLogCard";
import {
  generateDateID,
  getDayPostComments,
  addCommentToDayPost,
  editCommentOnDayPost,
  deleteCommentOnDayPost,
} from "../../services/dayPostService";
import { useAuth } from "../../contexts/AuthContext";
import { updateActivityLog } from "../../services/dayPostService";
import { getLinkedSessionIDs } from "../../utils/sessionLinks";
interface DayPostCardProps {
  dayPost: DayPost;
  userId: string;
  username?: string;
  imageUrl?: string;
  dailyInsight?: string;
  onAddLog: (dateID: string) => void;
  onDeleteLog?: (logId: string, logType: string, dateID: string) => void;
  onEditLog?: (logId: string, logType: string, logData: any, dateID: string) => void;
  onAddWellnessLog?: (dateID: string) => void;
  onOpenActivityLinkModal?: (
    activity: ActivityLog & { id: string },
    dayPost: DayPost,
  ) => void;
  onActivityDataChanged?: () => void;
}
const DayPostCard: React.FC<DayPostCardProps> = ({
  dayPost,
  userId,
  onAddLog,
  onDeleteLog,
  onEditLog,
  onAddWellnessLog,
  dailyInsight,
  onOpenActivityLinkModal,
  onActivityDataChanged,
}) => {
  const { currentUser, authState } = useAuth();
  // Events of the diary owner (event names) and the training type catalogue
  // — only needed when the day has a performance.
  const hasPerformance = dayPost.trainingLogs.some((l) => !!l.performanceType);
  const { events: userEvents, reload: reloadEvents } = useUserEvents(
    hasPerformance ? userId : undefined,
  );
  const [trainingTypeNamesById, setTrainingTypeNamesById] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    if (!hasPerformance) return;
    let cancelled = false;
    trainingTypeService
      .fetchTrainingTypes()
      .then((types) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        types.forEach((t) => {
          if (t.id) map[t.id] = t.name;
        });
        setTrainingTypeNamesById(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [hasPerformance]);
  const [comments, setComments] = useState<CommentWithUser[]>([]);
  const [showComments, setShowComments] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [commentMenuOpen, setCommentMenuOpen] = useState<string | null>(null);
  const [ignoringActivityKeys, setIgnoringActivityKeys] = useState<Set<string>>(
    new Set(),
  );

  const getActivityDedupKey = (activity: ActivityLog & { id: string }): string => {
    return [
      activity.terraActivityDataID || "",
      activity.provider || "",
      activity.startTime || "",
      activity.endTime || "",
      String(activity.duration ?? ""),
      (activity.sessionName || "").trim().toLowerCase(),
      String(activity.calories ?? ""),
      String(activity.workKilojoules ?? ""),
    ].join("|");
  };

  const unlinkedActivities = React.useMemo(() => {
    const allActivities = dayPost.activityLogs || [];

    const linkedDedupKeys = new Set<string>();
    allActivities.forEach((activity) => {
      if (activity.linkedTrainingLogID) {
        linkedDedupKeys.add(getActivityDedupKey(activity));
      }
    });

    const sourceActivities = allActivities.filter(
      (activity) => !activity.linkedTrainingLogID && !activity.ignore,
    );
    const seenKeys = new Set<string>();

    return sourceActivities.filter((activity) => {
      const dedupKey = getActivityDedupKey(activity);
      if (linkedDedupKeys.has(dedupKey)) return false;
      if (seenKeys.has(dedupKey)) return false;
      seenKeys.add(dedupKey);
      return true;
    });
  }, [dayPost.activityLogs]);

  const handleIgnoreActivity = useCallback(
    async (activity: ActivityLog & { id: string }) => {
      if (!userId) return;
      const selectedKey = getActivityDedupKey(activity);
      setIgnoringActivityKeys((prev) => new Set(prev).add(selectedKey));

      try {
        const allActivities = dayPost.activityLogs || [];
        const duplicateIds = allActivities
          .filter(
            (candidate) =>
              !candidate.linkedTrainingLogID &&
              !candidate.ignore &&
              getActivityDedupKey(candidate) === selectedKey,
          )
          .map((candidate) => candidate.id);

        const idsToIgnore = duplicateIds.length > 0 ? duplicateIds : [activity.id];

        await Promise.all(
          idsToIgnore.map((activityId) =>
            updateActivityLog(userId, activityId, { ignore: true }),
          ),
        );

        onActivityDataChanged?.();
      } catch (error) {
        console.error("Error ignoring activity:", error);
      } finally {
        setIgnoringActivityKeys((prev) => {
          const next = new Set(prev);
          next.delete(selectedKey);
          return next;
        });
      }
    },
    [userId, dayPost.activityLogs, onActivityDataChanged],
  );

  // Fetch comments on mount
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const fetchedComments = await getDayPostComments(
          userId,
          dayPost.dateID,
        );
        setComments(fetchedComments);
        // Auto-show comments if there are any
        if (fetchedComments.length > 0) {
          setShowComments(true);
        }
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        setLoadingComments(false);
      }
    };

    fetchComments();
  }, [userId, dayPost.dateID]);

  // Format date for display
  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (dateID === generateDateID(today)) {
      return "Today";
    } else if (dateID === generateDateID(yesterday)) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Check if the day has any logs
  const hasLogs =
    dayPost.trainingLogs.length > 0 ||
    dayPost.noteLogs.length > 0 ||
    dayPost.performanceLogs.length > 0 ||
    dayPost.recoveryLogs.length > 0 ||
    (dayPost.negativeInfluencesLogs?.length ?? 0) > 0 ||
    dayPost.niggleLogs.length > 0 ||
    dayPost.healthLogs.length > 0 ||
    dayPost.stepLogs.length > 0 ||
    dayPost.wellnessLogs.length > 0;

  // Check if this day is the start of a menstrual cycle
  const isCycleStartDay =
    dayPost.femaleCycleLogs && dayPost.femaleCycleLogs.length > 0;

  // Whether the athlete is marked injured for this day
  const isInjured = !!(
    dayPost.wellnessLogs &&
    dayPost.wellnessLogs.length > 0 &&
    dayPost.wellnessLogs[0].injured
  );

  // Debug: Log wellness logs for this day
  // console.log(`[DayPostCard] ${dayPost.dateID} - Full dayPost:`, {
  //   sessions: dayPost.sessions,
  //   dateID: dayPost.dateID,
  //   wellnessLogs: dayPost.wellnessLogs,
  //   hasWellnessLogs: dayPost.wellnessLogs && dayPost.wellnessLogs.length > 0,
  //   wellnessSoreness: dayPost.wellnessLogs?.[0]?.soreness,
  //   wellnessInjured: dayPost.wellnessLogs?.[0]?.injured,
  // });

  // Build a map of training log ID → linked sessions (a log can hold several,
  // plan 3a) so they nest inside their training log. Order follows the log's
  // `linkedSessionIDs` (primary first) when available.
  // Memoised so the arrays handed to TrainingLogCard keep their identity
  // between renders (its activity-name resolver keys effects on them).
  const linkedSessionsByTrainingLogId = useMemo(() => {
    const map = new Map<string, (typeof dayPost.sessions)[number][]>();
    dayPost.sessions.forEach((session) => {
      if (session.linkedTrainingLogID) {
        const list = map.get(session.linkedTrainingLogID) ?? [];
        list.push(session);
        map.set(session.linkedTrainingLogID, list);
      }
    });
    dayPost.trainingLogs.forEach((log) => {
      const list = map.get(log.id);
      if (!list || list.length < 2) return;
      const order = getLinkedSessionIDs(log);
      if (order.length === 0) return;
      list.sort((a, b) => {
        const ai = order.indexOf(a.id);
        const bi = order.indexOf(b.id);
        return (
          (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi)
        );
      });
    });
    return map;
  }, [dayPost.sessions, dayPost.trainingLogs]);
  const linkedSessionIds = new Set<string>();
  dayPost.sessions.forEach((session) => {
    if (session.linkedTrainingLogID) linkedSessionIds.add(session.id);
  });

  // Performance training logs (PerformanceLog.md §5.1) own their results:
  // results whose linkedTrainingLogID points at one are folded into its
  // Performance card and not rendered separately.
  const performanceTrainingLogIds = new Set(
    dayPost.trainingLogs.filter((log) => !!log.performanceType).map((l) => l.id),
  );
  const resultsByTrainingLogId = new Map<
    string,
    (typeof dayPost.performanceLogs)[number][]
  >();
  dayPost.performanceLogs.forEach((perfLog) => {
    const parent = perfLog.linkedTrainingLogID;
    if (parent && performanceTrainingLogIds.has(parent)) {
      const list = resultsByTrainingLogId.get(parent) ?? [];
      list.push(perfLog);
      resultsByTrainingLogId.set(parent, list);
    }
  });
  const nigglesByTrainingLogId = new Map<
    string,
    (typeof dayPost.niggleLogs)[number][]
  >();
  dayPost.niggleLogs.forEach((n) => {
    if (n.linkedTrainingLogID && performanceTrainingLogIds.has(n.linkedTrainingLogID)) {
      const list = nigglesByTrainingLogId.get(n.linkedTrainingLogID) ?? [];
      list.push(n);
      nigglesByTrainingLogId.set(n.linkedTrainingLogID, list);
    }
  });

  // Build a set of training log IDs that are linked to a legacy performance
  // log — these will be hidden from the feed (the result card shows them).
  const linkedTrainingLogIds = new Set<string>();
  dayPost.performanceLogs.forEach((perfLog) => {
    if (
      perfLog.linkedTrainingLogID &&
      !performanceTrainingLogIds.has(perfLog.linkedTrainingLogID)
    ) {
      linkedTrainingLogIds.add(perfLog.linkedTrainingLogID);
    }
  });

  // Collect all logs in specific order: Notes, Performance, Training, Wellness, Recovery, Niggle, Steps
  // When injured, niggles are shown inside the injury card instead
  // Sessions linked to a training log are excluded here — they render inside TrainingLogCard instead
  // Training logs linked to a performance log are excluded — they render inside PerformanceLogCard instead
  const allLogs = [
    // Sessions/activities are hidden from the feed — they are accessed via linked training/performance logs
    // 1. Notes
    ...dayPost.noteLogs.map((log) => ({
      type: "note",
      log,
      timestamp: log.timestamp,
      order: 1,
    })),
    // 2. Performance — performance training logs (with their results folded
    //    in), then legacy result-only performance logs
    ...dayPost.trainingLogs
      .filter((log) => performanceTrainingLogIds.has(log.id))
      .map((log) => ({
        type: "performanceTraining",
        log,
        timestamp: log.timestamp,
        order: 2,
      })),
    ...dayPost.performanceLogs
      .filter(
        (log) =>
          !log.linkedTrainingLogID ||
          !performanceTrainingLogIds.has(log.linkedTrainingLogID),
      )
      .map((log) => ({
        type: "performance",
        log,
        timestamp: log.timestamp,
        order: 2,
      })),
    // 3. Training (exclude performance training logs and training logs that
    //    are linked to a legacy performance log)
    ...dayPost.trainingLogs
      .filter(
        (log) =>
          !linkedTrainingLogIds.has(log.id) &&
          !performanceTrainingLogIds.has(log.id),
      )
      .map((log) => ({
        type: "training",
        log,
        timestamp: log.timestamp,
        order: 3,
      })),
    // 4. Wellness
    ...dayPost.wellnessLogs.map((log) => ({
      type: "wellness",
      log,
      timestamp: log.timestamp,
      order: 4,
    })),
    ...dayPost.recoveryLogs.map((log) => ({
      type: "recovery",
      log,
      timestamp: log.timestamp,
      order: 5,
    })),
    // 5b. Negative Influences
    ...(dayPost.negativeInfluencesLogs || []).map((log) => ({
      type: "negativeInfluences",
      log,
      timestamp: log.timestamp,
      order: 6,
    })),
    // 6. Niggle (renumber to order: 7)
    ...dayPost.niggleLogs.map((log) => ({
      type: "niggle",
      log,
      timestamp: log.timestamp,
      order: 7,
    })),
    // 7. Steps
    ...dayPost.stepLogs.map((log) => ({
      type: "step",
      log,
      timestamp: log.timestamp,
      order: 7,
    })),
    // 8. Health (keeping for compatibility) - only show latest
    ...(dayPost.healthLogs.length > 0
      ? [dayPost.healthLogs[dayPost.healthLogs.length - 1]].map((log) => ({
          type: "health",
          log,
          timestamp: log.timestamp,
          order: 8,
        }))
      : []),
  ].sort((a, b) => {
    // Primary sort by order
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    // Secondary sort by timestamp (newest first within same type)
    return b.timestamp - a.timestamp;
  });

  const renderLogCard = (logData: any, index: number) => {
    const { type, log } = logData;
    const key = log.id ?? `${type}-${index}`;

    // Use the log's own dateID (its original parent day post) so that
    // delete/edit operations target the correct day post reference list.
    // This matters for performance logs that may be displayed on a
    // different day than they were logged (see Feed.tsx redistribution).
    const logDateID = log.dateID || dayPost.dateID;

    // Create handlers for delete and edit
    const handleDelete = onDeleteLog
      ? (logId: string) => onDeleteLog(logId, type, logDateID)
      : undefined;
    const handleEdit = onEditLog
      ? (logId: string, logData: any) =>
          onEditLog(logId, type, logData, logDateID)
      : undefined;

    switch (type) {
      case "negativeInfluences":
        return (
          <NegativeInfluencesLogCard
            key={key}
            log={log}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      case "training":
        return (
          <TrainingLogCard
            key={key}
            log={log}
            linkedSessions={linkedSessionsByTrainingLogId.get(log.id)}
            ownerUserId={userId}
            // Removed onDelete and onEdit props to hide edit/remove buttons in feed
          />
        );
      case "performanceTraining":
        return (
          <PerformanceCard
            key={key}
            log={log}
            results={resultsByTrainingLogId.get(log.id) ?? []}
            niggles={nigglesByTrainingLogId.get(log.id) ?? []}
            events={userEvents}
            ownerUserId={currentUser?.uid === userId ? userId : undefined}
            onLinkChanged={() => {
              reloadEvents();
              onActivityDataChanged?.();
            }}
            trainingTypeName={
              log.trainingTypeID
                ? (trainingTypeNamesById[log.trainingTypeID] ?? null)
                : null
            }
            onEdit={
              onEditLog
                ? () => onEditLog(log.id, "training", log, logDateID)
                : undefined
            }
            onDelete={
              onDeleteLog
                ? () => onDeleteLog(log.id, "training", logDateID)
                : undefined
            }
          />
        );
      case "performance":
        return (
          <PerformanceLogCard
            key={key}
            log={log}
            userId={userId}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      case "recovery":
        return (
          <RecoveryLogCard
            key={key}
            log={log}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      case "health":
        return (
          <HealthLogCard
            key={key}
            log={log}
            dateID={dayPost.dateID}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      case "step":
        return (
          <StepLogCard
            key={key}
            log={log}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      case "wellness":
        return (
          <WellnessLogCard
            key={key}
            log={log}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      case "note":
        return (
          <NoteLogCard
            key={key}
            log={log}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      case "niggle":
        return (
          <NiggleLogCard
            key={key}
            log={log}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      default:
        return null;
    }
  };

  // Social media-style relative timestamps (e.g. "2h", "5m", "3d")
  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now();
    const diffMs = now - timestamp;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    const diffWeek = Math.floor(diffDay / 7);

    if (diffSec < 60) return "now";
    if (diffMin < 60) return `${diffMin}m`;
    if (diffHr < 24) return `${diffHr}h`;
    if (diffDay < 7) return `${diffDay}d`;
    if (diffWeek < 52) return `${diffWeek}w`;
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim() || !currentUser?.uid) {
      return;
    }

    setSubmittingComment(true);
    try {
      const addedComment = await addCommentToDayPost(
        userId,
        dayPost.dateID,
        newComment.trim(),
        currentUser.uid,
      );

      if (addedComment) {
        // Refresh comments to get the new one with user data
        const updatedComments = await getDayPostComments(
          userId,
          dayPost.dateID,
        );
        setComments(updatedComments);
        setNewComment("");
        setShowComments(true);
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = async (commentId: string, newText: string) => {
    if (!newText.trim()) return;
    try {
      await editCommentOnDayPost(
        userId,
        dayPost.dateID,
        commentId,
        newText.trim(),
      );
      const updatedComments = await getDayPostComments(userId, dayPost.dateID);
      setComments(updatedComments);
      setEditingCommentId(null);
      setEditCommentText("");
    } catch (error) {
      console.error("Error editing comment:", error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteCommentOnDayPost(userId, dayPost.dateID, commentId);
      const updatedComments = await getDayPostComments(userId, dayPost.dateID);
      setComments(updatedComments);
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  return (
    <div className="bg-base-100 rounded-none sm:rounded-xl shadow-sm border border-base-200 sm:border-base-200 p-4 pb-3 mb-2 max-w-3xl w-full mx-auto relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold tracking-wide uppercase text-base-content/50 font-geist">
          {formatDate(dayPost.dateID)}
        </p>
        {isCycleStartDay && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-geist font-medium bg-pink-500/10 text-pink-700 dark:text-pink-400 border border-pink-500/20">
            Cycle Start
          </span>
        )}
      </div>
      {/* Link Activities */}
      {unlinkedActivities.length > 0 && (
        <div className="bg-blue-300/20 border border-blue-700 px-4 py-4 rounded-lg mb-3">
          <div className="flex items-center space-x-2 mb-2">
            <HiPlus className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold text-base-content">
              Link Activities
            </h4>
          </div>
          <div className="space-y-2">
            {unlinkedActivities.map((activityLog) => {
              const isIgnoringActivity = ignoringActivityKeys.has(
                getActivityDedupKey(activityLog),
              );
              return (
                <div
                  key={activityLog.id}
                  className="bg-base-100 rounded-lg p-3 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-medium text-base-content">
                        New <span className="font-bold">{activityLog.provider || "wearable"}</span> activity
                      </h5>
                      <div className="flex items-center space-x-3 text-xs text-base-content/60">
                        {activityLog.duration && (
                          <span>{Math.round(activityLog.duration)} min</span>
                        )}
                        {activityLog.sessionName && (
                          <span className="font-medium text-base-content/80">
                            {activityLog.sessionName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() =>
                          onOpenActivityLinkModal?.(activityLog, dayPost)
                        }
                        disabled={isIgnoringActivity}
                        className="bg-blue-500 text-white px-2.5 py-1.5 rounded-md text-xs font-medium"
                      >
                        Link Training
                      </button>
                      <button
                        onClick={() => handleIgnoreActivity(activityLog)}
                        disabled={isIgnoringActivity}
                        className="px-2.5 py-1.5 rounded-md text-xs font-medium border border-base-300 text-base-content/70 bg-base-200 disabled:opacity-60"
                      >
                        {isIgnoringActivity ? "Ignoring..." : "Ignore"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!hasLogs ? (
        <button
          type="button"
          onClick={() => onAddLog(dayPost.dateID)}
          className="w-full text-left text-sm text-base-content/40 font-grotesk py-6 hover:text-base-content/60 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded"
        >
          No logs yet — tap to add one
        </button>
      ) : (
        <div className="flex flex-col">
          {allLogs.map((logData, index) => renderLogCard(logData, index))}
        </div>
      )}

      {/* Soreness & Injury Status */}
      {dayPost.wellnessLogs &&
        dayPost.wellnessLogs.length > 0 &&
        dayPost.wellnessLogs[0].soreness !== undefined &&
        dayPost.wellnessLogs[0].soreness > 0 && (
          <div className="flex flex-col gap-1.5">
            {/* Soreness Display */}
            <div className="px-3 py-2.5 rounded-lg bg-base-200/50 border border-transparent hover:border-base-300 transition-colors duration-150">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold tracking-wide uppercase text-base-content/70 font-geist">
                  Soreness
                </span>
                <span className="text-[11px] font-bold text-base-content bg-base-300/80 px-1.5 py-0.5 rounded font-geist">
                  {dayPost.wellnessLogs[0].soreness}/10
                </span>
              </div>
            </div>

            {/* Injured badge */}
            {isInjured && (
              <div className="px-3 py-2.5 rounded-lg border border-red-500/20 bg-red-500/5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500/15">
                    <span
                      className="w-2 h-2 rounded-full bg-red-500"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="text-xs font-semibold text-red-700 dark:text-red-400 font-geist">
                    Injured
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Action bar */}
      <div className="mt-3 pt-2 border-t border-base-200/60 flex justify-between items-center">
        <button
          type="button"
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-1.5 px-1 py-1 text-base-content/50 hover:text-primary rounded transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-label={`${showComments ? "Hide" : "Show"} comments`}
        >
          <HiChatBubbleOvalLeft className="w-3.5 h-3.5" />
          <span className="text-[11px] font-geist">
            {comments.length + (dailyInsight ? 1 : 0)}
          </span>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddLog(dayPost.dateID);
          }}
          className="flex items-center gap-1.5 text-[11px] font-geist text-base-content/50 hover:text-base-content/80 border border-base-200 hover:border-base-300 rounded-md px-2.5 py-1 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <HiPlus aria-hidden="true" className="w-3 h-3" />
          Add Log
        </button>
      </div>

      {/* Comments Section */}
      {(loadingComments || showComments) && (
        <div className="mt-3 border-t border-base-200 pt-3">
          {loadingComments ? (
            <div className="flex justify-center py-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-base-300 border-t-primary"></div>
            </div>
          ) : (
            <>
              {/* Comment input — social media style, at the top */}
              {currentUser && (
                <form
                  onSubmit={handleSubmitComment}
                  className="flex items-center gap-3 mb-3"
                >
                  <div className="w-7 h-7 rounded-full bg-base-300 flex-shrink-0 overflow-hidden">
                    {authState.user?.imageUrlTiny || authState.user?.imageUrl || currentUser.photoURL ? (
                      <img
                        src={authState.user?.imageUrlTiny || authState.user?.imageUrl || currentUser.photoURL || undefined}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-7 h-7 flex items-center justify-center">
                        <span className="text-base-content/60 text-xs font-semibold">
                          {currentUser.displayName?.[0] || "?"}
                        </span>
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 bg-transparent text-sm text-base-content placeholder:text-base-content/40 border-none outline-none py-1 focus-visible:outline-none"
                    disabled={submittingComment}
                    aria-label="Add a comment"
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim() || submittingComment}
                    className="text-sm font-semibold text-primary disabled:text-base-content/30 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded px-1"
                    aria-label="Post comment"
                  >
                    {submittingComment ? (
                      <span className="inline-block w-4 h-4 border-2 border-base-300 border-t-primary rounded-full animate-spin" />
                    ) : (
                      "Post"
                    )}
                  </button>
                </form>
              )}

              {/* Comments list — flat, inline style */}
              {comments.length > 0 || dailyInsight ? (
                <div className="space-y-2">
                  {/* AI Insight as first comment from Peak Insight */}
                  {dailyInsight && (
                    <div className="flex items-start gap-2.5">
                      <div className="flex-shrink-0 mt-0.5">
                        <HiSparkles className="w-5 h-5 text-primary/70" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm leading-snug">
                          <span className="font-semibold text-base-content">
                            Peak Insight
                          </span>{" "}
                          <span className="text-base-content/80">
                            {dailyInsight}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}

                  {comments.map((comment) => {
                    const isOwn = currentUser?.uid === comment.userID;
                    const isEditing = editingCommentId === comment.id;

                    return (
                      <div
                        key={comment.id}
                        className="flex items-start gap-2.5 group"
                      >
                        {/* Avatar */}
                        <div className="flex-shrink-0 mt-0.5">
                          {comment.user?.imageUrlTiny || comment.user?.imageUrl ? (
                            <img
                              src={comment.user.imageUrlTiny || comment.user.imageUrl}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-base-300 flex items-center justify-center">
                              <span className="text-base-content/60 text-[10px] font-semibold">
                                {comment.user?.name?.[0] || "?"}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Username + comment inline */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editCommentText}
                                onChange={(e) =>
                                  setEditCommentText(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    handleEditComment(
                                      comment.id,
                                      editCommentText,
                                    );
                                  if (e.key === "Escape") {
                                    setEditingCommentId(null);
                                    setEditCommentText("");
                                  }
                                }}
                                className="flex-1 bg-base-200 text-sm text-base-content rounded-md px-2 py-1 border border-base-300 outline-none focus:border-primary"
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleEditComment(comment.id, editCommentText)
                                }
                                className="p-1 text-primary hover:text-primary/80 transition-colors"
                                aria-label="Save edit"
                              >
                                <HiCheck className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingCommentId(null);
                                  setEditCommentText("");
                                }}
                                className="p-1 text-base-content/40 hover:text-base-content/60 transition-colors"
                                aria-label="Cancel edit"
                              >
                                <HiXMark className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm leading-snug">
                                <span className="font-semibold text-base-content">
                                  {comment.user?.name && comment.user?.surname
                                    ? `${comment.user.name} ${comment.user.surname}`
                                    : comment.user?.name || "User"}
                                </span>{" "}
                                <span className="text-base-content/80">
                                  {comment.comment}
                                </span>
                              </p>
                              <span className="text-[11px] text-base-content/40 mt-0.5 block select-none">
                                {formatRelativeTime(comment.timestamp)}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Actions menu — only for own comments, hidden during edit */}
                        {isOwn && !isEditing && (
                          <div className="relative flex-shrink-0 mt-0.5">
                            <button
                              type="button"
                              onClick={() =>
                                setCommentMenuOpen(
                                  commentMenuOpen === comment.id
                                    ? null
                                    : comment.id,
                                )
                              }
                              className="p-1 rounded-full text-base-content/0 group-hover:text-base-content/40 hover:!text-base-content/60 transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:text-base-content/40"
                              aria-label="Comment options"
                            >
                              <HiEllipsisHorizontal className="w-4 h-4" />
                            </button>
                            {commentMenuOpen === comment.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setCommentMenuOpen(null)}
                                />
                                <div className="absolute right-0 top-7 z-20 bg-base-100 border border-base-200 rounded-lg shadow-lg overflow-hidden min-w-[120px]">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingCommentId(comment.id);
                                      setEditCommentText(comment.comment);
                                      setCommentMenuOpen(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-base-content hover:bg-base-200 transition-colors"
                                  >
                                    <HiPencil className="w-3.5 h-3.5" />
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleDeleteComment(comment.id);
                                      setCommentMenuOpen(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                  >
                                    <HiTrash className="w-3.5 h-3.5" />
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-base-content/40 text-center py-2 select-none">
                  No comments yet — be the first!
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DayPostCard;
